import React from 'react';
import { graphql } from 'gatsby';

import Layout from '../layouts/Default';
import Billboard from '../components/Billboard';
import SEO from '../components/Seo';
import Wysiwyg from '../components/Wysiwyg';
import IconText from '../components/IconText';
import Testimonials from '../components/Testimonials';
import ImageText from '../components/ImageText';
import CrossSelling from '../components/CrossSelling';
import Banner from '../components/Banner';
import Distributor from '../components/Distributor';
import Gallery from '../components/Gallery';
import FeaturedText from '../components/FeaturedText';
import KPIs from '../components/HomeKPI';

import Faqs from '../components/Faqs';
import { cleanMetatags, getAlternateLinks } from '../ui/helpers';

interface IProps {
  data: any;
  pageContext: {
    id: string;
    translations: any;
    countryCode?: string;
  };
}

const BasicPage = ({ data, pageContext }: IProps) => {
  const { nodeBasicPage } = data;

  const regularMetatags = cleanMetatags(nodeBasicPage.metatag_normalized);
  const alternateLinks = getAlternateLinks(nodeBasicPage.metatag_normalized);

  let hrefLangs = [];

  nodeBasicPage.relationships?.field_hreflang &&
    nodeBasicPage.relationships.field_hreflang.map(element => {
      hrefLangs = hrefLangs.concat(getAlternateLinks(element.metatag_normalized));
    });

  const components = nodeBasicPage.relationships?.field_components;
  return (
    <Layout
      alternates={alternateLinks}
      lang={nodeBasicPage.langcode}
      country={pageContext.country}
      countryCode={pageContext.countryCode}
      translations={pageContext.translations}
      pageCta={nodeBasicPage?.field_cta}
      pageHref={nodeBasicPage?.path?.alias}
      isFranchise={
        nodeBasicPage.field_billboard_color === 'franchises_dark' ||
        nodeBasicPage.field_billboard_color === 'franchises_light'
      }>
      <SEO
        title={nodeBasicPage.title}
        lang={nodeBasicPage.langcode}
        metas={[...regularMetatags, ...alternateLinks, ...hrefLangs]}
      />
      <Billboard
        theme={nodeBasicPage.field_billboard_color}
        size={nodeBasicPage.field_billboard_size}
        claim={nodeBasicPage.field_pre}
        title={nodeBasicPage.title}
        cta={{
          uri: nodeBasicPage?.field_billboard_cta?.external,
          title: nodeBasicPage?.field_billboard_cta?.internal?.title
        }}
        breadcrumbs={{
          language: nodeBasicPage?.langcode,
          countryCode: pageContext.countryCode,
          pageHref: {
            alias: nodeBasicPage?.path?.alias,
            domain: nodeBasicPage?.relationships?.field_country?.field_domain?.uri
          }
        }}
        imageUrl={
          nodeBasicPage.relationships?.field_billboard_image?.relationships?.field_media_image
            ?.drupal_links
        }
        imageMobileUrl={
          nodeBasicPage.relationships?.field_billboard_mobile_image?.relationships
            ?.field_media_image?.drupal_links
        }
        imageAlt={nodeBasicPage.field_billboard_image?.field_media_image?.alt}
      />
      {components &&
        components
          .filter(component => component.internal)
          .map((component, i) => {
            const type = component.internal.type;
            const key = `${type}_${i}`;

            switch (type) {
              case 'paragraph__component_wysiwyg':
                return (
                  (component.field_text || component.field_title) && (
                    <Wysiwyg
                      component
                      key={key}
                      theme={component.field_color}
                      heading={component.field_heading}
                      title={component?.field_title}
                      text={component.field_text?.processed}
                    />
                  )
                );
              case 'paragraph__component_group_icon_text':
                const cards = [];
                component?.relationships?.field_icon_text.map(card => {
                  let splittedUrl, imageName, query, baseUrl, finalUrl;

                  const customEncodeURIComponent = (str) => {
                    return encodeURIComponent(str)
                      .replace(/\(/g, '%28')
                      .replace(/\)/g, '%29')
                      .replace(/'/g, '%27')
                      .replace(/!/g, '%21')
                      .replace(/\*/g, '%2A');
                  };

                  const getImageUrl = (links, size) => {
                    if (links && links[size] && links[size].href) {
                      return links[size].href;
                    }
                    return null;
                  };

                  const href = getImageUrl(card.relationships.field_image.relationships.field_media_image.drupal_links, 'original');

                  if (href) {
                    splittedUrl = href.split('/');
                    imageName = splittedUrl[splittedUrl.length - 1];
                    query = imageName.split('?')[1];
                    baseUrl = href.replace(imageName, '');
                    imageName = imageName.replace('?' + query, '');
                    finalUrl = baseUrl + customEncodeURIComponent(imageName) + '?' + query;
                  }

                  let cardObject = {
                    key: card.drupal_id,
                    field_title: card?.field_title,
                    field_text: card?.field_text?.value,
                    field_hash_link_element_id: card?.field_hash_link_active && card?.field_hash_link_element_id,
                    field_link: {
                      uri: card?.field_link?.external,
                      title: card?.field_link?.internal.title
                    },
                    field_media: {
                      alt: card?.relationships?.field_image?.field_media_image?.alt,
                      height: card?.relationships?.field_image?.field_media_image?.height,
                      url: finalUrl,
                      width: card?.relationships?.field_image?.field_media_image?.width
                    }
                  };
                  cards.push(cardObject);
                });
                return (
                  <IconText
                    key={component?.drupal_id}
                    theme={component?.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    text={component?.field_text?.value}
                    cards={cards}
                  />
                );
              case 'paragraph__component_testimonial':
                return (
                  <Testimonials
                    key={key}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    text={component.field_text.value}
                    name={component?.field_name}
                    role={component?.field_role}
                    imageUrl={
                      component?.relationships?.field_image?.relationships?.field_media_image
                        ?.drupal_links
                    }
                    imageAlt={component?.relationships?.field_media?.field_media_image?.alt}
                    videoUrl={component.relationships?.field_image?.field_media_oembed_video}
                  />
                );
              case 'paragraph__componente_imagen_texto':
                return (
                  <ImageText
                    key={key}
                    elementId={component?.field_element_id}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    position={component?.field_subtitle_position}
                    subtitle={component?.field_imagen_text_subtitle}
                    title={component?.field_title}
                    direction={component.field_direction}
                    text={component.field_text.processed}
                    link={{
                      uri: component?.field_link?.external,
                      title: component?.field_link?.internal.title
                    }}
                    imageUrl={
                      component?.relationships?.field_image?.relationships?.field_media_image
                        ?.drupal_links
                    }
                    imageAlt={component?.relationships?.field_media?.field_media_image?.alt}
                    centeredContent={component?.field_subtitle_position === 'pretitle'}
                    fullwidth={true}
                  />
                );
              case 'paragraph__component_faqs':
                const boxes = [];
                component?.relationships?.field_faq.map((box, i) => {
                  boxes.push({
                    key: component?.drupal_id + '_' + i,
                    title: box.field_title,
                    text: box.field_text?.processed
                  });
                });
                return (
                  <Faqs
                    key={component?.drupal_id}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    boxes={boxes}
                  />
                );
              case 'paragraph__component_cross_selling':
                return (
                  <CrossSelling
                    key={component?.drupal_id}
                    theme={component.field_color}
                    title={component?.field_title}
                    text={component?.field_text?.value}
                    cards={component?.relationships?.field_cross_selling.map((card, i) => ({
                      key: card?.drupal_id + '_' + i,
                      title: card.field_title,
                      text: card.field_text?.value,
                      link: {
                        uri: card.field_link?.external,
                        title: card.field_link?.internal?.title
                      },
                      imageUrl:
                        card.relationships.field_image.relationships.field_media_image
                          ?.drupal_links,
                      imageAlt: card.relationships.field_image.relationships.field_media_image?.alt
                    }))}
                  />
                );
              case 'paragraph__component_hubspot_banner':
                return (
                  <Banner
                    key={component?.drupal_id}
                    theme={component.field_color}
                    hubspot={{
                      desktop: component?.field_desktop_hubspot_banner,
                      mobile: component?.field_mobile_hubspot_banner
                    }}
                  />
                );
              case 'paragraph__component_distributor':
                return (
                  <Distributor
                    key={component?.drupal_id}
                    theme={component?.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    cards={component?.relationships?.field_cards.map((card, i) => ({
                      key: card?.drupal_id + '_' + i,
                      theme: card?.field_color,
                      title: card?.field_title,
                      text: card?.field_card_text,
                      link: {
                        uri: card.field_cta?.external,
                        title: card.field_cta?.internal?.title
                      },
                      imageUrl:
                        card.relationships.field_image.relationships.field_media_image
                          ?.drupal_links,
                      imageAlt: card.relationships.field_image.relationships.field_media_image?.alt
                    }))}
                  />
                );
              case 'paragraph__component_gallery':
                return (
                  <Gallery
                    key={component?.drupal_id}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    items={component?.relationships?.field_media?.map((item, i) => {
                      if (item.relationships?.field_image) {
                        switch (item.relationships.field_image?.internal?.type) {
                          case 'media__media_image':
                            return {
                              key: item.relationships.field_image?.drupal_id + '_' + i,
                              type: 'image',
                              description: item?.field_card_text,
                              itemUrl:
                                item.relationships.field_image.relationships.field_media_image
                                  ?.drupal_links,
                              itemAlt: item.relationships.field_image.field_media_image?.alt
                            };
                          case 'media__media_youtube':
                            return {
                              key: item.relationships.field_image?.drupal_id + '_' + i,
                              type: 'video',
                              description: item?.field_card_text,
                              itemUrl: item.relationships.field_image.field_media_oembed_video,
                              itemThumbnail:
                                item.relationships.field_image.relationships.thumbnail.drupal_links
                            };
                        }
                      }
                    })}
                  />
                );
              case 'paragraph__component_featured_text':
                return (
                  <FeaturedText
                    key={component?.drupal_id}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    subtitle={component?.field_feature_text_subtitle}
                    title={component?.field_title}
                    text={component?.field_featured_text}
                    textColor={component?.field_typography_color}
                  />
                );
              case 'paragraph__component_kpis':
                return (
                  <KPIs
                    key={component?.drupal_id}
                    theme={component.field_color}
                    heading={component?.field_heading}
                    title={component?.field_title}
                    blocks={component?.relationships?.field_kpis.map((kpi, i) => ({
                      key: kpi?.drupal_id + '_' + i,
                      theme: kpi.field_color,
                      bigText: kpi.field_title,
                      smallText: kpi.field_subtitle,
                      imageAlt: kpi.relationships?.field_media_image?.alt
                    }))}
                  />
                );
            }
          })}
    </Layout>
  );
};

export const query = graphql`
  query ($id: String) {
    nodeBasicPage(id: { eq: $id }) {
      id
      drupal_id
      title
      langcode
      status
      path {
        alias
      }
      field_pre
      field_billboard_color
      field_billboard_size
      field_billboard_cta {
        internal {
          title
        }
        external
      }
      field_cta {
        external
        internal {
          title
        }
      }
      relationships {
        field_billboard_image {
          field_media_image {
            alt
            height
            width
          }
          relationships {
            field_media_image {
              drupal_links {
                fullwidth {
                  href
                  meta {
                    height
                    width
                  }
                }
                large {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium {
                  href
                  meta {
                    height
                    width
                  }
                }
                original {
                  href
                  meta {
                    height
                    width
                  }
                }
                small {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                fullwidth_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                large_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                original_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                small_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
              }
            }
          }
        }
        field_billboard_mobile_image {
          field_media_image {
            alt
            height
            width
          }
          relationships {
            field_media_image {
              drupal_links {
                fullwidth {
                  href
                  meta {
                    height
                    width
                  }
                }
                large {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium {
                  href
                  meta {
                    height
                    width
                  }
                }
                original {
                  href
                  meta {
                    height
                    width
                  }
                }
                small {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                fullwidth_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                large_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                original_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                small_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
              }
            }
          }
        }
        field_components {
          ... on paragraph__component_wysiwyg {
            field_color
            field_heading
            field_title
            field_text {
              processed
              format
            }
            internal {
              type
            }
          }
          ... on paragraph__component_group_icon_text {
            drupal_id
            field_color
            field_heading
            field_title
            field_text {
              value
            }
            relationships {
              field_icon_text {
                drupal_id
                field_title
                field_hash_link_active
                field_hash_link_element_id
                field_text {
                  value
                }
                field_link {
                  internal {
                    title
                  }
                  external
                }
                relationships {
                  field_image {
                    field_media_image {
                      alt
                      height
                      width
                    }
                    relationships {
                      field_media_image {
                        drupal_links {
                          original {
                            href
                          }
                          original_webp {
                            href
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            internal {
              type
            }
          }
          ... on paragraph__component_testimonial {
            id
            field_heading
            field_name
            field_color
            field_role
            field_title
            field_text {
              value
            }
            relationships {
              field_image {
                ... on media__media_image {
                  field_media_image {
                    alt
                    height
                    width
                  }
                  relationships {
                    field_media_image {
                      drupal_links {
                        large {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        large_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                      }
                    }
                  }
                }
                ... on media__media_youtube {
                  relationships {
                    thumbnail {
                      drupal_links {
                        large {
                          href
                        }
                        medium {
                          href
                        }
                        original {
                          href
                        }
                        small {
                          href
                        }
                        xlarge {
                          href
                        }
                        xsmall {
                          href
                        }
                        xxsmall {
                          href
                        }
                        large_webp {
                          href
                        }
                        medium_webp {
                          href
                        }
                        original_webp {
                          href
                        }
                        small_webp {
                          href
                        }
                        xlarge_webp {
                          href
                        }
                        xsmall_webp {
                          href
                        }
                        xxsmall_webp {
                          href
                        }
                      }
                    }
                  }
                  field_media_oembed_video
                }
              }
            }
            internal {
              type
            }
          }
          ... on paragraph__componente_imagen_texto {
            id
            field_color
            field_direction
            field_heading
            field_element_id
            field_link {
              internal {
                title
              }
              external
            }
            field_text {
              processed
            }
            field_imagen_text_subtitle
            field_title
            field_subtitle_position
            internal {
              type
            }
            relationships {
              field_image {
                field_media_image {
                  alt
                  height
                  width
                }
                relationships {
                  field_media_image {
                    drupal_links {
                      large {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      medium {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      original {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      small {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xlarge {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xsmall {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xxsmall {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      large_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      medium_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      original_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      small_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xlarge_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xsmall_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                      xxsmall_webp {
                        href
                        meta {
                          height
                          width
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on paragraph__component_faqs {
            drupal_id
            field_title
            field_heading
            field_color
            relationships {
              field_faq {
                field_text {
                  processed
                }
                field_title
              }
            }
            internal {
              type
            }
          }
          ... on paragraph__component_cross_selling {
            field_color
            field_text {
              value
            }
            field_title
            relationships {
              field_cross_selling {
                field_link {
                  internal {
                    title
                  }
                  external
                }
                field_text {
                  value
                }
                field_title
                relationships {
                  field_image {
                    relationships {
                      field_media_image {
                        drupal_links {
                          large {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          medium {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          original {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          small {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xlarge {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xsmall {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xxsmall {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          large_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          medium_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          original_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          small_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xlarge_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xsmall_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xxsmall_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                        }
                      }
                    }
                    field_media_image {
                      alt
                    }
                  }
                }
              }
            }
            drupal_id
            internal {
              type
            }
          }
          ... on paragraph__component_hubspot_banner {
            field_mobile_hubspot_banner
            field_desktop_hubspot_banner
            field_color
            drupal_id
            internal {
              type
            }
          }
          ... on paragraph__component_distributor {
            drupal_id
            internal {
              type
            }
            field_heading
            field_title
            field_color
            relationships {
              field_cards {
                drupal_id
                field_color
                field_card_text
                field_title
                field_cta {
                  external
                  internal {
                    title
                    uri
                  }
                }
                relationships {
                  field_image {
                    field_media_image {
                      alt
                    }
                    relationships {
                      field_media_image {
                        drupal_links {
                          large {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          medium {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          original {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          small {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xlarge {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xsmall {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xxsmall {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          large_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          medium_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          original_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          small_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xlarge_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xsmall_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                          xxsmall_webp {
                            href
                            meta {
                              height
                              width
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on paragraph__component_gallery {
            field_title
            field_heading
            field_color
            drupal_id
            relationships {
              field_media {
                relationships {
                  field_image {
                    ... on media__media_image {
                      field_media_image {
                        alt
                      }
                      relationships {
                        field_media_image {
                          drupal_links {
                            original {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            small {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            medium {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            large {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xlarge {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xsmall {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xxsmall {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            original_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            small_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            medium_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            large_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xlarge_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xsmall_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            xxsmall_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                          }
                        }
                      }
                      drupal_id
                      internal {
                        type
                      }
                    }
                    ... on media__media_youtube {
                      drupal_id
                      internal {
                        type
                      }
                      field_media_oembed_video
                      relationships {
                        thumbnail {
                          drupal_links {
                            original {
                              href
                              meta {
                                height
                                width
                              }
                            }
                            original_webp {
                              href
                              meta {
                                height
                                width
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                field_card_text
              }
            }
            internal {
              type
            }
          }
          ... on paragraph__component_featured_text {
            drupal_id
            field_color
            field_featured_text
            field_heading
            field_feature_text_subtitle
            field_title
            field_typography_color
            internal {
              type
            }
          }
          ... on paragraph__component_kpis {
            field_title
            field_color
            field_heading
            drupal_id
            relationships {
              field_kpis {
                drupal_id
                field_title
                field_subtitle
                field_color
              }
            }
            internal {
              type
            }
          }
        }
        field_country {
          field_country_code
          field_domain {
            uri
          }
        }
        field_hreflang {
          id
          title
          langcode
          metatag_normalized {
            attributes {
              content
              href
              hreflang
              name
              property
              rel
            }
          }
          relationships {
            field_country {
              field_domain {
                uri
              }
            }
          }
        }
      }
      metatag_normalized {
        attributes {
          content
          href
          hreflang
          name
          property
          rel
        }
      }
    }
  }
`;

export default BasicPage;
